import { useEffect, useState } from 'react'
import { FileText } from 'lucide-react'
import { UserRole } from '@app/types'
import { DATADOG_ACTIONS } from '@app/app-constants'
import { logDataDogAction } from '@app/utils/logger'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from '@app/components/ui/Toast/toast'
import { EvidenceDocument } from '../documents.types'
import { DOCUMENT_ROLE_LABELS_MAP } from '../documents.constants'
import { formatDate, formatFileSize, getDocumentType } from '../documents.utils'
import { downloadDocument } from '../service/documents.service'
import { useFetchDocumentPermissions, useFetchDocumentPreviewUrl } from '../hooks/useDocuments'
import { useFetchFolders } from '../hooks/useFolders'
import { useDocumentRoleOptions } from '../hooks/useDocumentRoleOptions'
import { PdfPreview } from './pdf-preview'
import { ImagePreview } from './image-preview'
import { GeoreferencePreviewMap } from './georeference-preview-map'
import { MarkdownRenderer } from './markdown-renderer'

type DocumentPreviewDialogProps = {
  document: EvidenceDocument | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

// SVG excluded — more XML attack surface than raster formats for user-uploaded content.
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp'])

// Client-side only — the preview endpoint has no size limit of its own.
const IMAGE_PREVIEW_MAX_BYTES = 10 * 1024 * 1024

export const DocumentPreviewDialog = ({
  document,
  open,
  onOpenChange,
}: DocumentPreviewDialogProps) => {
  const [previewFailed, setPreviewFailed] = useState(false)
  const documentType = document ? getDocumentType(document.file.originalFilename) : ''
  const isPdf = documentType === 'pdf'
  const isImage = IMAGE_EXTENSIONS.has(documentType)
  const imageTooLarge = isImage && (document?.file.sizeBytes ?? 0) > IMAGE_PREVIEW_MAX_BYTES
  const canPreview = isPdf || (isImage && !imageTooLarge)
  const previewUrlQuery = useFetchDocumentPreviewUrl(document?.id, open && canPreview)
  // isError = hop 1 (resolving the URL) failed; previewFailed = hop 2 (rendering it) failed.
  const showPreview = canPreview && !previewFailed && !previewUrlQuery.isError

  const { data: folders = [] } = useFetchFolders()
  const roleOptions = useDocumentRoleOptions()
  const shouldFetchPermissions = open && document?.visibility === 'organisation'
  const permissionsQuery = useFetchDocumentPermissions(document?.id, shouldFetchPermissions)

  // Per-document, not just on close — the dialog can swap documents without `open` toggling false.
  useEffect(() => setPreviewFailed(false), [document?.id])

  // Fires once per open, not once per mount — the dialog can swap documents without open toggling false.
  useEffect(() => {
    if (!open || !document) return
    logDataDogAction(DATADOG_ACTIONS.DOCUMENT_VIEWED, {
      id: document.id,
      title: document.title,
      mimeType: document.file.mimeType,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, document?.id])

  if (!document) return null

  const handleDownload = () =>
    downloadDocument(document.id, document.file.originalFilename)
      .then(() =>
        logDataDogAction(DATADOG_ACTIONS.DOCUMENT_DOWNLOADED, {
          id: document.id,
          title: document.title,
          mimeType: document.file.mimeType,
        }),
      )
      .catch(() =>
        toast({
          description: 'Failed to download document.',
          position: 'top-right',
          type: 'error',
        }),
      )

  // Mirrors the Permissions step's "WHO CAN SEE THIS" list.
  const permissionsLabel =
    document.visibility === 'personal'
      ? 'Private'
      : permissionsQuery.isLoading
        ? 'Loading…'
        : [
            DOCUMENT_ROLE_LABELS_MAP[UserRole.ADMIN],
            ...(permissionsQuery.data?.user_roles ?? []).map(
              (roleId) => roleOptions.find((role) => role.id === roleId)?.label ?? roleId,
            ),
          ].join(', ')

  const collectionName = document.collectionId
    ? (folders.find((folder) => folder.id === document.collectionId)?.name ?? '—')
    : '—'

  const metadataRows: { label: string; value: string }[] = [
    { label: 'Owner', value: document.ownerEmail ?? 'Deleted user' },
    { label: 'Organisation', value: document.organisation ?? '—' },
    { label: 'Date published', value: formatDate(document.publishedAt) },
    { label: 'Date uploaded', value: formatDate(document.uploadedAt) },
    { label: 'File size', value: formatFileSize(document.file.sizeBytes) },
    { label: 'Permissions', value: permissionsLabel },
    { label: 'Source', value: document.source ?? '—' },
    {
      label: 'Geo ref',
      value:
        document.georeference.type === 'NONE' ? '—' : (document.georeference.searchLabel ?? 'Set'),
    },
    { label: 'Collections', value: collectionName },
    { label: 'Notes', value: document.notesMd || '—' },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[85vh] p-0 flex overflow-hidden gap-0">
        <div className="flex-1 min-w-0 bg-neutral-100 overflow-y-auto p-6">
          {showPreview && previewUrlQuery.data && isPdf && (
            <PdfPreview url={previewUrlQuery.data.url} onError={() => setPreviewFailed(true)} />
          )}

          {showPreview && previewUrlQuery.data && isImage && (
            <ImagePreview
              url={previewUrlQuery.data.url}
              alt={document.title}
              onError={() => setPreviewFailed(true)}
            />
          )}

          {showPreview && !previewUrlQuery.data && <Skeleton className="h-full w-full" />}

          {!showPreview && (
            <div className="h-full rounded-md border bg-white flex flex-col items-center justify-center gap-2">
              <FileText className="h-8 w-8 text-neutral-400" />
              <p className="text-sm text-neutral-500">
                {imageTooLarge
                  ? 'This image is too large to preview — download to view.'
                  : isPdf
                    ? "This PDF couldn't be previewed — download to view."
                    : isImage
                      ? "This image couldn't be previewed — download to view."
                      : 'Preview isn’t available for this format — download to view.'}
              </p>
            </div>
          )}
        </div>

        <div className="w-[420px] shrink-0 border-l flex flex-col overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b text-left space-y-1">
            <DialogTitle>{document.title}</DialogTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="neutral" size="sm">
                {document.category}
              </Badge>
              <span>{getDocumentType(document.file.originalFilename).toUpperCase()}</span>
              <span>{formatFileSize(document.file.sizeBytes)}</span>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4">
            {document.descriptionMd && (
              <MarkdownRenderer markdown={document.descriptionMd} className="text-neutral-700" />
            )}

            <GeoreferencePreviewMap georeference={document.georeference} />

            <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
              {metadataRows.map((row) => (
                <div className="contents" key={row.label}>
                  <dt className="text-neutral-500">{row.label}</dt>
                  <dd className="min-w-0 truncate" title={row.value}>
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>

            {document.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {document.tags.map((tag) => (
                  <Badge key={tag} variant="neutral" size="sm">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 p-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button onClick={handleDownload}>Download</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
